import { Icon } from "@iconify/react/dist/iconify.js";
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import ThemeToggleButton from "../helper/ThemeToggleButton";
import { usePermissions } from "../context/PermissionContext";
import LoadingComponent from "../components/ui/LoadingComponent";
import Notification from "../components/Notification";
import Profile from "../components/Profile";
import { useUser } from "../context/UserContext";
import { routePermissions } from "../components/ProtectedRoute";
import { handleCustomResponse } from "../utils/response";

const API_BASE_URL =
  "https://pevbackend.codovio.com/public/uploads/profile_pictures/";

const MasterLayout = ({ children }) => {
  const { hasPermission, isAuthenticated, isLoading, refreshAuthStatus } =
    usePermissions();
  const { userData, updateUserData } = useUser();
  const navigate = useNavigate();
  const [sidebarActive, setSidebarActive] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [isSidebarHover, setIsSidebarHover] = useState(false);
  const [isMobileHover, setIsMobileHover] = useState(false);
  const location = useLocation();

  const username = userData.username || "User";
  const role = userData.role_name || "Role Name";
  const profilePicture = userData.profile_picture || null;

  const initials = username
    .split(" ")
    .map((name) => name[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const getImageUrl = (imageValue) => {
    if (!imageValue) return null;
    if (imageValue.startsWith("http")) return imageValue;
    return `${API_BASE_URL}${imageValue}`;
  };

  const imageUrl = getImageUrl(profilePicture);

  // Check if it's mobile screen
  const isMobileScreen = () => {
    return window.innerWidth <= 768; // You can adjust this breakpoint as needed
  };

  // Function to close mobile menu when link is clicked
  const handleLinkClick = () => {
    if (isMobileScreen() && mobileMenu) {
      setMobileMenu(false);
    }
  };

  const handleLogout = async () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("token_expiry");
    localStorage.removeItem("user");
    updateUserData({});
    await refreshAuthStatus();
    handleCustomResponse("success", "Logout Successful");
    navigate("/sign-in", { replace: true });
  };

  useEffect(() => {
    const handleDropdownClick = (event) => {
      event.preventDefault();
      const clickedLink = event.currentTarget;
      const clickedDropdown = clickedLink.closest(".dropdown");

      if (!clickedDropdown) return;

      const isActive = clickedDropdown.classList.contains("open");

      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        dropdown.classList.remove("open");
        const submenu = dropdown.querySelector(".sidebar-submenu");
        if (submenu) {
          submenu.style.maxHeight = "0px";
        }
      });

      if (!isActive) {
        clickedDropdown.classList.add("open");
        const submenu = clickedDropdown.querySelector(".sidebar-submenu");
        if (submenu) {
          submenu.style.maxHeight = `${submenu.scrollHeight}px`;
        }
      }
    };

    const dropdownTriggers = document.querySelectorAll(
      ".sidebar-menu .dropdown > a, .sidebar-menu .dropdown > Link"
    );

    dropdownTriggers.forEach((trigger) => {
      trigger.addEventListener("click", handleDropdownClick);
    });

    const openActiveDropdown = () => {
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        const submenuLinks = dropdown.querySelectorAll(".sidebar-submenu li a");
        submenuLinks.forEach((link) => {
          if (
            link.getAttribute("href") === location.pathname ||
            link.getAttribute("to") === location.pathname
          ) {
            dropdown.classList.add("open");
            const submenu = dropdown.querySelector(".sidebar-submenu");
            if (submenu) {
              submenu.style.maxHeight = `${submenu.scrollHeight}px`;
            }
          }
        });
      });
    };

    openActiveDropdown();

    return () => {
      dropdownTriggers.forEach((trigger) => {
        trigger.removeEventListener("click", handleDropdownClick);
      });
    };
  }, [location.pathname]);

  const sidebarControl = () => {
    setSidebarActive(!sidebarActive);
  };

  const mobileMenuControl = () => {
    setMobileMenu(!mobileMenu);
  };

  const handleOverlayClick = (e) => {
    if (mobileMenu && e.target === e.currentTarget) {
      setMobileMenu(false);
    }
  };

  const hasAnyPermission = (routes) => {
    return routes.some((route) => hasPermission(routePermissions[route]));
  };

  if (isLoading) {
    return <LoadingComponent />;
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <section
      className={mobileMenu ? "overlay active" : "overlay"}
      onClick={handleOverlayClick}
    >
      <aside
        className={
          sidebarActive
            ? "sidebar active"
            : mobileMenu
            ? "sidebar sidebar-open"
            : "sidebar"
        }
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={mobileMenuControl}
          type="button"
          className="sidebar-close-btn"
        >
          <Icon icon="radix-icons:cross-2" />
        </button>
        <div>
          <Link className="sidebar-logo">
            <img
              src="/assets/images/clbg.png"
              alt="site logo"
              className="light-logo"
            />
            <img
              src="/assets/images/clbg.png"
              alt="site logo"
              className="dark-logo"
            />
            <img
              src="/assets/images/clbg.png"
              alt="site logo"
              className="logo-icon"
            />
          </Link>
        </div>

        <div className="sidebar-profile">
          <div className="profile-info d-flex align-items-center gap-3">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="profile"
                className="rounded-circle"
                style={{
                  width: "40px",
                  height: "40px",
                  objectFit: "cover",
                  border: "none",
                  cursor: "default",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : (
              <button
                className="profile-initials"
                style={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: "#3a59d1",
                  color: "white",
                  fontSize: "1rem",
                  fontWeight: "600",
                  borderRadius: "50%",
                  border: "none",
                  cursor: "default",
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {initials}
              </button>
            )}
            <button
              className="profile-initials"
              style={{
                width: "40px",
                height: "40px",
                backgroundColor: "#3a59d1",
                color: "white",
                fontSize: "1rem",
                fontWeight: "600",
                borderRadius: "50%",
                border: "none",
                cursor: "default",
                textAlign: "center",
                display: imageUrl ? "none" : "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {initials}
            </button>
            <div className="profile-details">
              <h6 className="text-lg text-primary-light fw-semibold mb-2">
                {username}
              </h6>
              <span className="text-secondary-light fw-medium text-sm">
                {role
                  .split("_")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")}
              </span>
            </div>
          </div>
        </div>

        <div className="sidebar-menu-area">
          {/* Dashboard */}
          <ul className="sidebar-menu" id="sidebar-menu">
            <li className="dropdown">
              <li>
                <NavLink
                  to="/"
                  className={(navData) =>
                    navData.isActive ? "active-page" : ""
                  }
                  onClick={handleLinkClick}
                >
                  <Icon icon="lucide:bar-chart-3" className="menu-icon" />
                  <span>Main Dashboard</span>
                </NavLink>
              </li>
            </li>
          </ul>
          {/* Billing */}
          {hasAnyPermission([
            "/tax-types",
            "/tax-rates",
            "/view_billing_summary",
          ]) && (
            <ul className="sidebar-menu mt-2" id="sidebar-menu">
              <li className="dropdown">
                <Link to="#">
                  <Icon icon="lucide:banknote" className="menu-icon text-green-500" />


                  <span>Billings</span>
                </Link>
                <ul className="sidebar-submenu">
                  {hasPermission("view_tax_types") && (
                    <li>
                      <NavLink
                        to="/tax-types"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Tax Types
                      </NavLink>
                    </li>
                  )}
                  {hasPermission("view_tax_rates") && (
                    <li>
                      <NavLink
                        to="/tax-rates"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Tax Rates
                      </NavLink>
                    </li>
                  )}
                  {hasPermission("view_billing_summary") && (
                    <li>
                      <NavLink
                        to="/view_billing_summary"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Billing Summary
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>
            </ul>
          )}
          {/* Clients */}
          {hasAnyPermission([
            "/all-clients",
            "/client-branches",
            "/branch-location",
            "/client-roles",
            "/staff",
            "/client-permissions",
            "/customer-profile",
            "/client-management",
          ]) && (
            <ul className="sidebar-menu mt-2" id="sidebar-menu">
              <li className="dropdown">
                <Link to="#">
                  <Icon icon="lucide:handshake" className="menu-icon" />

                  <span>Clients</span>
                </Link>
                <ul className="sidebar-submenu">
                  {hasPermission("view_client") && (
                    <li>
                      <NavLink
                        to="/all-clients"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Clients
                      </NavLink>
                    </li>
                  )}

                  {hasPermission("view_client_branches") && (
                    <li>
                      <NavLink
                        to="/client-branches"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Client Branches
                      </NavLink>
                    </li>
                  )}
                  {hasPermission("view_branch_location") && (
                    <li>
                      <NavLink
                        to="/branch-location"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Branch Location
                      </NavLink>
                    </li>
                  )}
                  {hasPermission("view_client_roles") && (
                    <li>
                      <NavLink
                        to="/client-roles"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Client Roles
                      </NavLink>
                    </li>
                  )}
                  {hasPermission("view_staff") && (
                    <li>
                      <NavLink
                        to="/staff"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Staff
                      </NavLink>
                    </li>
                  )}
                  {hasPermission("view_customer_profile") && (
                    <li>
                      <NavLink
                        to="/customer-profile"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Customer Profile
                      </NavLink>
                    </li>
                  )}
                  {hasPermission("view_client_permission") && (
                    <li>
                      <NavLink
                        to="/client-permissions"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Client Permissions
                      </NavLink>
                    </li>
                  )}
                  {/* {hasPermission("manage_slots") && (
                    <li>
                      <NavLink
                        to="/slots"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Slots
                      </NavLink>
                    </li>
                  )} */}
                  {hasPermission("view_client_management") && (
                    <li>
                      <NavLink
                        to="/client-management"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Client Management
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>
            </ul>
          )}
          {/* Geographical */}
          {hasAnyPermission(["/all-cities", "/all-countries"]) && (
            <ul className="sidebar-menu mt-2" id="sidebar-menu">
              <li className="dropdown">
                <Link to="#">
                  <Icon icon="lucide:map-pin" className="menu-icon" />
                  <span>Geographical</span>
                </Link>
                <ul className="sidebar-submenu">
                  {hasPermission("view_countries") && (
                    <li>
                      <NavLink
                        to="/all-countries"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Countries
                      </NavLink>
                    </li>
                  )}
                  {hasPermission("view_cities") && (
                    <li>
                      <NavLink
                        to="/all-cities"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Cities
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>
            </ul>
          )}
          {/* Vehicle Master Data */}
          {hasAnyPermission([
            "/vehicle-types",
            "/vehicle-makes",
            "/vehicle-models",
            "/plate-authorities",
          ]) && (
            <ul className="sidebar-menu mt-2" id="sidebar-menu">
              <li className="dropdown">
                <Link to="#">
                  <Icon icon="lucide:car" className="menu-icon" />

                  <span>Vehicles</span>
                </Link>
                <ul className="sidebar-submenu">
                  {hasPermission("view_vehicle_types") && (
                    <li>
                      <NavLink
                        to="/vehicle-types"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Vehicle Types
                      </NavLink>
                    </li>
                  )}
                  {hasPermission("view_vehicle_makes") && (
                    <li>
                      <NavLink
                        to="/vehicle-makes"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Vehicle Makes
                      </NavLink>
                    </li>
                  )}
                  {hasPermission("view_vehicle_models") && (
                    <li>
                      <NavLink
                        to="/vehicle-models"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Vehicle Models
                      </NavLink>
                    </li>
                  )}
                  {hasPermission("view_plate_authority") && (
                    <li>
                      <NavLink
                        to="/plate-authorities"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Plate Authorities
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>
            </ul>
          )}
          {/* Workflow */}
          {hasAnyPermission([
            "/workflow-list",
            "/workflow-assign",
            "/workflows-suggestions",
          ]) && (
            <ul className="sidebar-menu mt-2" id="sidebar-menu">
              <li className="dropdown">
                <Link to="#">
                  <Icon icon="lucide:repeat-2" className="menu-icon" />
                  <span>Workflows</span>
                </Link>
                <ul className="sidebar-submenu">
                  {hasPermission("view_workflow") && (
                    <li>
                      <NavLink
                        to="/workflow-list"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Workflows
                      </NavLink>
                    </li>
                  )}
                  {hasPermission("view_assign_workflow") && (
                    <li>
                      <NavLink
                        to="/workflow-assign"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Assign Workflows
                      </NavLink>
                    </li>
                  )}
                  {hasPermission("view_workflow_suggestion_step") && (
                    <li>
                      <NavLink
                        to="/workflows-suggestions"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Workflows Suggestions
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>
            </ul>
          )}
          {/* Planning */}
          {hasAnyPermission(["/all-plans"]) && (
            <ul className="sidebar-menu mt-2" id="sidebar-menu">
              <li className="dropdown">
                <Link to="#">
                 <Icon icon="lucide:layers" className="menu-icon" />

                  <span>Planning</span>
                </Link>
                <ul className="sidebar-submenu">
                  {hasPermission("view_plan") && (
                    <li>
                      <NavLink
                        to="/all-plans"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Plan
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>
            </ul>
          )}
          {/* Users */}
          {hasAnyPermission([
            "/add-user-permission",
            "/all-roles",
            "/all-users",
            "/user-permissions",
          ]) && (
            <ul className="sidebar-menu mt-2" id="sidebar-menu">
              <li className="dropdown">
                <Link to="#">
                  <Icon icon="mage:users" className="menu-icon" />
                  <span>Users</span>
                </Link>
                <ul className="sidebar-submenu">
                  {hasPermission("insert_permission") && (
                    <li>
                      <NavLink
                        to="/add-user-permission"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Add Permissions
                      </NavLink>
                    </li>
                  )}
                  {hasPermission("view_roles") && (
                    <li>
                      <NavLink
                        to="/all-roles"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Roles
                      </NavLink>
                    </li>
                  )}
                  {hasPermission("view_user") && (
                    <li>
                      <NavLink
                        to="/all-users"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        Users
                      </NavLink>
                    </li>
                  )}
                  {hasPermission("view_user_permission") && (
                    <li>
                      <NavLink
                        to="/user-permissions"
                        className={(navData) =>
                          navData.isActive ? "active-page" : ""
                        }
                        onClick={handleLinkClick}
                      >
                        <i className="text-primary-600 w-auto" />
                        User Permissions
                      </NavLink>
                    </li>
                  )}
                </ul>
              </li>
            </ul>
          )}
        </div>

        <div className="sidebar-logout">
          <div className="d-flex align-items-center justify-content-center">
            <button
              onClick={handleLogout}
              className="logout-btn btn bg-danger-50 text-danger-600 hover-bg-danger-100 d-flex align-items-center justify-content-center gap-2 w-100"
            >
              <Icon icon="lucide:log-out" className="icon text-xl" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      <main
        className={sidebarActive ? "dashboard-main active" : "dashboard-main"}
      >
        <div className="navbar-header">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-4">
                <button
                  type="button"
                  className="sidebar-toggle"
                  onClick={sidebarControl}
                  onMouseEnter={() => setIsSidebarHover(true)}
                  onMouseLeave={() => setIsSidebarHover(false)}
                >
                  <Icon
                    icon={
                      isSidebarHover
                        ? sidebarActive
                          ? "lucide:arrow-left"
                          : "lucide:arrow-right"
                        : "heroicons:bars-3-solid"
                    }
                    className="icon"
                  />
                </button>
                <button
                  onClick={mobileMenuControl}
                  type="button"
                  className="sidebar-mobile-toggle"
                  onMouseEnter={() => setIsMobileHover(true)}
                  onMouseLeave={() => setIsMobileHover(false)}
                >
                  <Icon
                    icon={
                      isMobileHover
                        ? mobileMenu
                          ? "lucide:arrow-left"
                          : "lucide:arrow-right"
                        : "heroicons:bars-3-solid"
                    }
                    className="icon"
                  />
                </button>
                {/* <div className="navbar-search">
                  <input
                    type="text"
                    placeholder="Search..."
                    style={{
                      width: "24.25rem",
                      height: "2.5rem",
                      backgroundColor: "var(--neutral-50)",
                      border: "1px solid var(--input-form-light)",
                      borderRadius: "0.5rem",
                      padding: "0.3125rem 1.25rem 0.3125rem 2.625rem",
                      color: "var(--text-primary-light)",
                    }}
                  />
                  <Icon
                    icon="lucide:search"
                    className="icon"
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "0.9375rem",
                      transform: "translateY(-50%)",
                      fontSize: "1.125rem",
                      color: "var(--text-primary-light, #000)",
                    }}
                  />
                </div> */}
              </div>
            </div>
            <div className="col-auto">
              <div className="navbar-icons-container">
                <Notification />
                <ThemeToggleButton />
                <Profile />
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-main-body">{children}</div>
      </main>
    </section>
  );
};

export default MasterLayout;
